import LinkifyText from "components/app/linkify/LinkifyText";
import React, { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import socket from "services/socket";

interface FollowingLivePlayerProps {
  post: any;
  playerControlRef?: React.MutableRefObject<{
    pause: () => void;
    play: () => void;
    goLive: () => void;
  } | null>;
}

const FollowingLivePlayer: React.FC<FollowingLivePlayerProps> = ({ post, playerControlRef }) => {
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<any>(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [streamEnded, setStreamEnded] = useState(false);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.3 });

  useEffect(() => {
    if (!post || !inView || streamEnded || !playerContainerRef.current) return;

    const mediaUrl = post.media_url;
    const streamingKey = post.media_key;

    const loadClappr = () => {
      const player = new window.Clappr.Player({
        source: mediaUrl,
        parentId: `#${playerContainerRef.current!.id}`,
        autoPlay: true,
        mute: true,
        width: "100%",
        height: "100%",
      });

      playerInstanceRef.current = player;

      const observer = new MutationObserver(() => {
        const el = playerContainerRef.current?.querySelector(".player-poster.clickable");
        if (el) {
          el.remove();
          observer.disconnect();
          console.log("[Clappr] Poster play button removed via MutationObserver");
        }
      });

      observer.observe(playerContainerRef.current!, {
        childList: true,
        subtree: true,
      });

      if (playerControlRef) {
        playerControlRef.current = {
          pause: () => player.pause(),
          play: () => player.play(),
          goLive: () => {
            const duration = player.getDuration?.();
            if (duration) player.seek(duration);
            player.play();
          },
        };
      }

      player.on(window.Clappr.Events.PLAYER_READY, () => {
        console.log("Video ready");
      });

      //   console.log("player : ", player.core.mediaControl);
      console.log("player : ", player);
    };

    if (!window.Clappr) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/clappr@latest/dist/clappr.min.js";
      script.async = true;
      script.onload = loadClappr;
      document.body.appendChild(script);
    } else {
      loadClappr();
    }

    socket.on("viewerCountUpdate", setViewerCount);
    socket.on("OnFlipFlopStreamEnded", () => {
      setStreamEnded(true);
      playerInstanceRef.current?.destroy?.();
    });

    socket.emit("viewerJoin", streamingKey);

    return () => {
      socket.emit("viewerLeave", streamingKey);
      socket.off("viewerCountUpdate");
      socket.off("OnFlipFlopStreamEnded");
      playerInstanceRef.current?.destroy?.();
      if (playerControlRef) playerControlRef.current = null;
    };
  }, [inView, post, streamEnded, playerControlRef]);

  return (
    <div ref={inViewRef} className="flex w-full h-full relative overflow-hidden mt-2 rounded-lg" onClick={(e) => e.stopPropagation()}>
      <style>
        {`
    .player-poster.clickable,
    .player-poster.clickable .play-wrapper,
    .poster-icon {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `}
      </style>

      <div className="absolute top-3 right-3 flex space-x-2 z-20">
        <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded">LIVE</div>
        <div className="bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded flex items-center space-x-1">
          <svg className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" fill="none">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z"
            />
          </svg>
          <span>{viewerCount}</span>
        </div>
      </div>

      {post.media_title && (
        <div className={`absolute w-full bg-gradient-to-t from-black/45 p-2 z-10 max-h-[50vh] overflow-y-auto bottom-[50px]`} onWheel={(e) => e.stopPropagation()}>
          <div className="mt-2 text-white">
            <LinkifyText text={post.media_title} target="_blank" />
          </div>
        </div>
      )}

      <div className="w-full h-full" id={`clappr-player-${post.media_key}`} ref={playerContainerRef} />

      {streamEnded && <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center text-white text-2xl z-50">🔴 This live stream has ended.</div>}
    </div>
  );
};

export default FollowingLivePlayer;

